import { sitInstance } from '../api/instances';
import { toastSuccess, toastError } from '../const/toast';

/**
 * Fetch metadata for all available reference data types
 */
export const fetchAvailableTypes = () => sitInstance.get('/reference_data/available');

/**
 * Fetch records for a specific reference data type
 * @param {string} type - The reference data type (e.g., 'districts', 'agency_types')
 * @param {Object} params - Query parameters
 * @param {boolean} params.withDiscarded - Include soft-deleted records
 * @param {string} params.sliceType - Filter by context: 'all', 'system', 'organization', 'division'
 * @param {string} params.sliceId - The org_uid or division_uid for filtering
 */
export const fetchRecords = (type, params = {}) => {
  const { withDiscarded = true, sliceType = 'all', sliceId } = params;

  const queryParams = {
    with_discarded: withDiscarded,
    slice_type: sliceType,
  };

  if (sliceId && (sliceType === 'organization' || sliceType === 'division')) {
    queryParams.slice_id = sliceId;
  }

  return sitInstance.get(`/reference_data/${type}`, { params: queryParams });
};

/**
 * Fetch a single record by ID
 * @param {string} type - The reference data type
 * @param {string} id - The record ID
 */
export const fetchRecord = (type, id) => sitInstance.get(`/reference_data/${type}/${id}`);

/**
 * Create a new record
 * @param {string} type - The reference data type
 * @param {Object} payload - The record data
 */
export const createRecord = (type, payload) =>
  sitInstance.post(`/reference_data/${type}`, { record: payload })
    .then((response) => {
      toastSuccess('Record created');
      return response;
    })
    .catch((error) => {
      toastError('Error creating record');
      throw error;
    });

/**
 * Update an existing record
 * @param {string} type - The reference data type
 * @param {string} id - The record ID
 * @param {Object} payload - The updated record data
 */
export const updateRecord = (type, id, payload) =>
  sitInstance.put(`/reference_data/${type}/${id}`, { record: payload })
    .then((response) => {
      toastSuccess('Record updated');
      return response;
    })
    .catch((error) => {
      toastError('Error updating record');
      throw error;
    });

/**
 * Soft delete a record
 * @param {string} type - The reference data type
 * @param {string} id - The record ID
 */
export const deleteRecord = (type, id) =>
  sitInstance.delete(`/reference_data/${type}/${id}`)
    .then((response) => {
      toastSuccess('Record disabled');
      return response;
    })
    .catch((error) => {
      toastError('Error disabling record');
      throw error;
    });

/**
 * Reactivate a soft-deleted record
 * @param {string} type - The reference data type
 * @param {string} id - The record ID
 */
export const reactivateRecord = (type, id) =>
  sitInstance.patch(`/reference_data/${type}/${id}/reactivate`)
    .then((response) => {
      toastSuccess('Record reactivated');
      return response;
    })
    .catch((error) => {
      toastError('Error reactivating record');
      throw error;
    });
